import { useState, useEffect, useRef, useCallback } from 'react'
import * as api from '../../../lib/api'

// ============================================
// HLS Player — Живая трансляция через Ivideon Open API
// Поддержка H.264 (HLS.js), H.265+HEVC (нативный/HLS.js),
// H.265 без HEVC → snapshot-fallback (обновляемый JPEG)
// ============================================
function HlsPlayer({ hlsUrl, cameraFullId, muted = true, onMutedChange, autoPlay = true, className = '', onReady, onError }) {
  const videoRef = useRef(null)
  const hlsRef = useRef(null)
  const mountedRef = useRef(true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [showH265Tip, setShowH265Tip] = useState(false)
  const [snapshotMode, setSnapshotMode] = useState(false)
  const [snapshotUrl, setSnapshotUrl] = useState(null)
  const snapshotIntervalRef = useRef(null)
  const timeoutRef = useRef(null)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      destroyAll()
    }
  }, [])

  const destroyHls = useCallback(() => {
    if (hlsRef.current) {
      try { hlsRef.current.destroy() } catch (e) {}
      hlsRef.current = null
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const stopSnapshots = useCallback(() => {
    if (snapshotIntervalRef.current) {
      clearInterval(snapshotIntervalRef.current)
      snapshotIntervalRef.current = null
    }
  }, [])

  const destroyAll = useCallback(() => {
    destroyHls()
    stopSnapshots()
  }, [destroyHls, stopSnapshots])

  const isHevcSupported = () => {
    if (typeof window === 'undefined') return false;
    if (typeof MediaSource !== 'undefined' && typeof MediaSource.isTypeSupported === 'function') {
      if (MediaSource.isTypeSupported('video/mp4; codecs="hvc1"') || 
          MediaSource.isTypeSupported('video/mp4; codecs="hev1"')) {
        return true;
      }
    }
    try {
      const video = document.createElement('video')
      const canPlay1 = video.canPlayType('video/mp4; codecs="hvc1"')
      const canPlay2 = video.canPlayType('video/mp4; codecs="hev1"')
      if (canPlay1 === 'probably' || canPlay1 === 'maybe' || canPlay2 === 'probably' || canPlay2 === 'maybe') {
        return true;
      }
    } catch (e) {}
    return false;
  }

  // Загрузка снапшота (JPEG) и запуск автообновления
  const startSnapshotMode = useCallback(async () => {
    if (!cameraFullId) {
      setError(true)
      setLoading(false)
      onError?.()
      return
    }

    setSnapshotMode(true)
    setShowH265Tip(true) // постоянная плашка в snapshot-режиме

    const loadSnapshot = async () => {
      try {
        const result = await api.getIvideonSnapshot(cameraFullId)
        if (mountedRef.current && result.url) {
          // Добавляем timestamp для обхода кэша браузера
          setSnapshotUrl(result.url + '&_t=' + Date.now())
          setLoading(false)
          setError(false)
          onReady?.()
        }
      } catch (err) {
        console.warn('[Snapshot] Load error:', err)
        // Не показываем ошибку при обновлении — оставляем последний снапшот
        if (!snapshotUrl && mountedRef.current) {
          setLoading(false)
          setError(true)
          onError?.()
        }
      }
    }

    // Первая загрузка
    await loadSnapshot()

    // Автообновление каждые 3 секунды
    stopSnapshots()
    snapshotIntervalRef.current = setInterval(() => {
      if (mountedRef.current) loadSnapshot()
    }, 10000)
  }, [cameraFullId, stopSnapshots])

  useEffect(() => {
    if (!hlsUrl) return
    destroyAll()
    setLoading(true)
    setError(false)
    setShowH265Tip(false)
    setSnapshotMode(false)
    setSnapshotUrl(null)

    const initPlayer = async () => {
      try {
        const isH265 = hlsUrl.includes('vcodec=h265')
        const hasHevc = isHevcSupported()

        // H.265 без HEVC → snapshot-fallback
        if (isH265 && !hasHevc) {
          console.info('[HLS] H.265 detected, HEVC not supported → switching to snapshot mode')
          await startSnapshotMode()
          return
        }

        // Стандартный Hls.js или нативный плеер (для H.264 или H.265 с аппаратным декодированием)
        const Hls = (await import('hls.js')).default

        if (!mountedRef.current || !videoRef.current) return

        // Нативная поддержка HLS (Safari)
        if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
          videoRef.current.src = hlsUrl
          videoRef.current.addEventListener('loadedmetadata', () => {
            if (mountedRef.current) {
              setLoading(false)
              if (isH265) {
                setShowH265Tip(true)
                setTimeout(() => {
                  if (mountedRef.current) setShowH265Tip(false)
                }, 8000)
              }
              onReady?.()
              if (autoPlay) videoRef.current.play().catch(() => {})
            }
          }, { once: true })
          videoRef.current.addEventListener('error', () => {
            if (mountedRef.current) {
              setLoading(false)
              setError(true)
              onError?.()
            }
          }, { once: true })
          return
        }

        if (!Hls.isSupported()) {
          console.warn('[HLS] Not supported')
          setLoading(false)
          setError(true)
          onError?.()
          return
        }

        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          liveSyncDurationCount: 3,
          liveMaxLatencyDurationCount: 6,
          maxBufferLength: 10,
          maxMaxBufferLength: 30,
          startLevel: -1,
        })

        hls.loadSource(hlsUrl)
        hls.attachMedia(videoRef.current)

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (!mountedRef.current) return
          setLoading(false)
          if (isH265) {
            setShowH265Tip(true)
            setTimeout(() => {
              if (mountedRef.current) setShowH265Tip(false)
            }, 8000)
          }
          onReady?.()
          if (autoPlay) {
            videoRef.current.muted = muted
            videoRef.current.play().catch(() => {})
          }
        })

        hls.on(Hls.Events.ERROR, (event, data) => {
          if (!mountedRef.current) return
          if (data.fatal) {
            console.warn('[HLS] Fatal error:', data.type, data.details)
            if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
              setTimeout(() => {
                if (mountedRef.current && hlsRef.current) {
                  hlsRef.current.startLoad()
                }
              }, 3000)
            } else {
              setLoading(false)
              setError(true)
              onError?.()
              hls.destroy()
            }
          }
        })

        hlsRef.current = hls
      } catch (err) {
        console.error('[HLS] Init error:', err)
        if (mountedRef.current) {
          setLoading(false)
          setError(true)
          onError?.()
        }
      }
    }

    initPlayer()
    return () => destroyAll()
  }, [hlsUrl])

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted
  }, [muted])

  return (
    <div className={`hls-player-wrapper ${className}`}>
      {loading && (
        <div className="camera-loader-overlay stream-loading">
          <div className="spinner" />
          <span>{snapshotMode ? 'Загрузка снимка...' : 'Загрузка трансляции...'}</span>
        </div>
      )}
      {error && (
        <div className="camera-preview-error">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <span>Трансляция временно недоступна</span>
        </div>
      )}

      {/* Бейдж: LIVE или СНИМКИ */}
      {!error && !loading && (
        snapshotMode
          ? <div className="camera-preview-snapshot-badge"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg> СНИМКИ</div>
          : <div className="camera-preview-live-badge">● LIVE</div>
      )}

      {/* Информационная плашка для H.265 */}
      {showH265Tip && !loading && !error && (
        <div className={`h265-tip-overlay ${snapshotMode ? 'snapshot-mode' : 'native'}`}>
          <span className="h265-tip-icon">{snapshotMode ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg> : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>}</span>
          <span className="h265-tip-text">
            {snapshotMode
              ? 'Камера передаёт видео в формате H.265, который не поддерживается вашим браузером. Отображаются обновляемые снимки. Для полноценного видео переключите кодек камеры на H.264 в Личном кабинете Ivideon.'
              : 'H.265 с аппаратным ускорением. Для лучшей плавности переключите кодек на H.264 в ЛК Ivideon.'
            }
          </span>
        </div>
      )}

      {/* Контент: снапшот или видео */}
      {snapshotMode ? (
        <div className={`snapshot-fallback-container ${!loading && !error ? 'visible' : ''}`}>
          {snapshotUrl && (
            <img
              src={snapshotUrl}
              alt="Live preview"
              className="snapshot-fallback-img"
            />
          )}
        </div>
      ) : (
        <video
          ref={videoRef}
          className={`camera-preview-video ${!loading && !error ? 'visible' : ''}`}
          muted={muted}
          playsInline
          autoPlay={autoPlay}
        />
      )}
    </div>
  )
}

export default HlsPlayer
